import { Box, Button, ButtonGroup, Card, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useEffect, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { useDispatch, useSelector } from 'react-redux';
import { CardBgcolor, MainBorderRadius, PageHeader } from '../../app/AppStyles';
import { setAlertMessage, setAlertSeverity, setAlertStatus } from '../subcomponents/alertSlice';
import { putEmployee, resetEmployeeStatus } from './employeeSlice';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    width: { xs: '90%', md: 'auto' },
    maxHeight: '90%',
    boxShadow: 24,
};

export default function EmployeeEditCard({ selectedEmployee, handleClose, addNew }) {
    const dispatch = useDispatch();

    const { user } = useSelector((state) => state.user);
    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { employeeUpdate } = useSelector((state) => state.employees);
    const { aspNetUser } = useSelector((state) => state.settings.app);

    const [newEmployee, setNewEmployee] = useState({});
    const [changed, setChanged] = useState(false);

    // Put status callback
    useEffect(() => {
        if (employeeUpdate.status === "success") {
            dispatch(resetEmployeeStatus());
            dispatch(setAlertStatus(true))
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.SAVE_SUCCESS}`));
            dispatch(setAlertSeverity("success"));
            handleClose();
        } else if (employeeUpdate.status === "failed") {
            dispatch(setAlertStatus(true))
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.ERROR_SAVE_FAILED}`));
            dispatch(setAlertSeverity("error"));

        };
    }, [dispatch, handleClose, employeeUpdate.status]);

    // Selected employee state update 
    useEffect(() => {
        if (selectedEmployee)
            setNewEmployee(selectedEmployee);
    }, [selectedEmployee]);

    // Keep track of changed state 
    useEffect(() => {
        if (newEmployee !== selectedEmployee) {
            setChanged(true);
        } else {
            setChanged(false);
        };
    }, [selectedEmployee, newEmployee]);

    // Update click function
    const updateClick = () => {
        newEmployee.ModifiedBy = `AP/${user.number}`;
        dispatch(putEmployee(newEmployee));
    };

    function EmployeeCheckbox({ label, value, disabled }) {
        return (
            <FormControlLabel
                label={label}
                control={<Checkbox
                    checked={newEmployee[value] ? newEmployee[value] : false}
                    disabled={disabled}
                    onChange={(event) => setNewEmployee({
                        ...newEmployee,
                        [value]: event.target.checked ? true : false
                    })}
                />}
            />
        );
    }

    return (
        <Box sx={{ ...modalStyle }}>
            <Card sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', p: 2 }}>
                <Scrollbars autoHeight autoHeightMin="100%" autoHeightMax="100%" autoHideTimeout={1000} autoHideDuration={200}>
                    <Grid container spacing={1}>
                        <Grid size={12}>
                            <Typography variant="h6" sx={{ ...PageHeader }}>{resourceFinal[appLanguage].words.EMPLOYEE_ROLES} {addNew ? resourceFinal[appLanguage].words.ADD.toLowerCase() : resourceFinal[appLanguage].words.EDIT.toLowerCase()}</Typography>
                            <Typography variant="subtitle1" sx={{ ...PageHeader, color: 'primary.light' }}>{selectedEmployee.fullname}</Typography>
                        </Grid>
                        <Grid size={4}>
                            <Stack spacing={1} sx={{ bgcolor: CardBgcolor, borderRadius: MainBorderRadius, height: 1 }}>
                                <Typography variant="h6" sx={{ ...PageHeader }}>{resourceFinal[appLanguage].words.MANAGEMENT}</Typography>
                                <EmployeeCheckbox
                                    label={`${resourceFinal[appLanguage].words.ROLE_ACTIVITIES}`}
                                    value={'activities'}
                                />
                                <EmployeeCheckbox
                                    label={`${resourceFinal[appLanguage].words.ROLE_TIMETABLES}`}
                                    value={'timetables'}
                                />
                                <EmployeeCheckbox
                                    label={`${resourceFinal[appLanguage].words.ROLE_EMPLOYEES}`}
                                    value={'employees'}
                                />
                                <EmployeeCheckbox
                                    label={`${resourceFinal[appLanguage].words.ROLE_ORGANISATION}`}
                                    value={'organisation'}
                                />
                                <EmployeeCheckbox
                                    label={`${resourceFinal[appLanguage].words.ROLE_PROGRAMS}`}
                                    value={'programs'}
                                />
                                <EmployeeCheckbox
                                    label={`${resourceFinal[appLanguage].words.ROLE_WEBMASTER}`}
                                    value={'webmaster'}
                                />
                            </Stack>
                        </Grid>
                        <Grid size={4}>
                            <Stack spacing={1} sx={{ bgcolor: CardBgcolor, borderRadius: MainBorderRadius, height: 1 }}>
                                <Typography variant="h6" sx={{ ...PageHeader }}>{resourceFinal[appLanguage].words.QUALIFICATIONS}</Typography>
                                <EmployeeCheckbox
                                    label={`${resourceFinal[appLanguage].words.ROLE_SUBSCRIPTIONS}`}
                                    value={'subscriptions'}
                                />
                                <EmployeeCheckbox
                                    label={`${resourceFinal[appLanguage].words.ROLE_MESSAGES}`}
                                    value={'messages'}
                                />
                                <EmployeeCheckbox
                                    label={`${resourceFinal[appLanguage].words.ROLE_PLANNING}`}
                                    value={'planning'}
                                />
                                <EmployeeCheckbox
                                    label={`${resourceFinal[appLanguage].words.ROLE_ATTENDANCES}`}
                                    value={'attendances'}
                                />
                                <EmployeeCheckbox
                                    label={`${resourceFinal[appLanguage].words.ROLE_REPORTS}`}
                                    value={'reports'}
                                />
                                <EmployeeCheckbox
                                    label={`${resourceFinal[appLanguage].words.ROLE_STUDENTS}`}
                                    value={'students'}
                                />
                                <EmployeeCheckbox
                                    label={`${resourceFinal[appLanguage].words.ROLE_SUPERVISOR}`}
                                    value={'supervisor'}
                                />
                            </Stack>
                        </Grid>
                        <Grid container size={4} spacing={1}>
                            <Grid size={12} sx={{ bgcolor: CardBgcolor, borderRadius: MainBorderRadius }}>
                                <Stack spacing={1}>
                                    <Typography variant="h6" sx={{ ...PageHeader }}>{resourceFinal[appLanguage].words.OTHERS}</Typography>
                                    <EmployeeCheckbox
                                        label={`${resourceFinal[appLanguage].words.CONTACTPERSON}`}
                                        value={'contactPerson'}
                                    />
                                </Stack>
                            </Grid>
                            {aspNetUser?.role?.includes("Sysadmin") && (
                                <Grid size={12} sx={{ bgcolor: CardBgcolor, borderRadius: MainBorderRadius }}>
                                    <Stack spacing={1}>
                                        <Typography variant="h6" sx={{ ...PageHeader }}>{resourceFinal[appLanguage].messages.USER_STATUS}</Typography>
                                        <EmployeeCheckbox
                                            label={`${resourceFinal[appLanguage].words.ACTIVE}`}
                                            value={'isActive'}
                                        />
                                        <EmployeeCheckbox
                                            label={`${resourceFinal[appLanguage].words.TEST} ${resourceFinal[appLanguage].words.USER}`}
                                            value={'isTestUser'}
                                        />
                                    </Stack>
                                </Grid>
                            )}
                            {aspNetUser?.role?.includes("Admin") && (
                                <Grid size={12} sx={{ bgcolor: CardBgcolor, borderRadius: MainBorderRadius }}>
                                    <Stack spacing={1}>
                                        <Typography variant="h6" sx={{ ...PageHeader }}>{resourceFinal[appLanguage].words.ROLE_ADMIN}</Typography>
                                        <EmployeeCheckbox
                                            label={`${resourceFinal[appLanguage].words.ROLE_ADMIN}`}
                                            value={'admin'}
                                        />
                                        <EmployeeCheckbox
                                            label={`${resourceFinal[appLanguage].words.ROLE_SYSADMIN}`}
                                            value={'sysadmin'}
                                        />
                                    </Stack>
                                </Grid>
                            )}
                        </Grid>
                        <Grid size={12}>
                            <Stack spacing={1}>
                                <ButtonGroup sx={{ justifyContent: 'center' }}>
                                    <Button variant="outlined" color="error" onClick={handleClose}>{resourceFinal[appLanguage].words.CANCEL}</Button>
                                    {!addNew && <Button disabled={!changed} variant="outlined" color="primary" onClick={updateClick}>{resourceFinal[appLanguage].words.SAVE}</Button>}
                                </ButtonGroup>
                            </Stack>
                        </Grid>
                    </Grid>
                </Scrollbars>
            </Card>
        </Box >
    );
}
